import cuid from "cuid";
import { filter, get, isNil, last, sortBy, uniq } from "lodash";
import { useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { OFFERS } from "utils/api/graphql/queries/offers";
import { JOB_LIST } from "utils/constants";
import {
  buildCombinations,
  toPersistedCombinations,
} from "utils/helpers/combinations";
import { filterByConditions } from "../../../..";
import useInsurancePlans from "../../../Manual/Solyon/Comparison/Widgets/Upsells/hooks/useInsurancePlans";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const combinationKey = (combination) =>
  `${
    get(combination, "baseInsurancePlanId") ||
    get(combination, "baseInsurancePlan.id") ||
    ""
  }|${
    get(combination, "complementaryInsurancePlanId") ||
    get(combination, "complementaryInsurancePlan.id") ||
    "none"
  }`;

const Upsells = ({
  processId,
  project,
  onNext,
  onBack,
  currentStatus,
  tracer,
}) => {
  const baseInsurancePlanId = get(project, "fields.lastSelected.0");

  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);

  const [selectedModules, setSelectedModules] = useState(
    get(project, "fields.upsells", {}),
  );
  const [selectedCombinationKeys, setSelectedCombinationKeys] = useState(() => {
    const persisted = get(project, "fields.combinations", []);
    return persisted.map(combinationKey);
  });
  const [hasInteractedWithCombinations, setHasInteractedWithCombinations] =
    useState(() => get(project, "fields.combinations", []).length > 0);

  const [getOffers, { loading: offersLoading }] = useLazyQuery(OFFERS);
  const { selectedInsurancePlans, fetchMainOffers, loading } =
    useInsurancePlans({
      insurancePlan: { id: baseInsurancePlanId },
      getOffers,
      project,
      setSelectedModules,
      offerWhere: [
        {
          offerInsurancePlans: {
            weight: get(project, "fields.hasUpsells") ? 1 : 2,
          },
        },
      ],
    });
  useQuery(OFFERS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        AND: [
          {
            offerInsurancePlans: {
              insurancePlan: { id: baseInsurancePlanId },
            },
          },
          { offerInsurancePlans: { main: true } },
        ],
      },
    },
    onCompleted: (offers) => {
      fetchMainOffers(offers);
    },
  });

  const { data: generalCostsData, loading: generalCostsLoading } = useQuery(
    GENERAL_COSTS,
    {
      skip: !baseInsurancePlanId,
      variables: {
        where: {
          project: { id: project.id },
          insurancePlan: { id: baseInsurancePlanId },
        },
      },
    },
  );

  const baseGeneralCost = useMemo(
    () => get(generalCostsData, "generalCosts.data.0"),
    [generalCostsData],
  );

  const combinations = useMemo(() => {
    if (!baseGeneralCost) return [];
    return buildCombinations({
      bases: [baseGeneralCost],
      upsellsByBase: selectedModules,
    });
  }, [baseGeneralCost, selectedModules]);

  const selectedCombinationIds = useMemo(() => {
    if (combinations.length === 0) return [];
    if (!hasInteractedWithCombinations) {
      return combinations.map(({ id }) => id);
    }
    return combinations
      .filter((combination) =>
        selectedCombinationKeys.includes(combinationKey(combination)),
      )
      .map(({ id }) => id);
  }, [combinations, selectedCombinationKeys, hasInteractedWithCombinations]);

  const handleModule = ({ module }) => {
    const id = get(module, "insurancePlan.id");
    setSelectedModules((previous) => {
      const currentSelected = get(previous, baseInsurancePlanId, []);
      const exist = currentSelected.find(
        ({ insurancePlan }) => insurancePlan.id === id,
      );
      if (exist) {
        return {
          ...previous,
          [baseInsurancePlanId]: currentSelected.filter(
            ({ insurancePlan }) => insurancePlan.id !== id,
          ),
        };
      }
      return {
        ...previous,
        [baseInsurancePlanId]: [...currentSelected, module],
      };
    });
  };

  const handleCombination = (combinationId) => {
    const combination = combinations.find(({ id }) => id === combinationId);
    if (!combination) return;
    const key = combinationKey(combination);
    setHasInteractedWithCombinations(true);
    setSelectedCombinationKeys((previous) => {
      const baseKeys = hasInteractedWithCombinations
        ? previous
        : combinations.map(combinationKey);
      return baseKeys.includes(key)
        ? baseKeys.filter((existing) => existing !== key)
        : [...baseKeys, key];
    });
  };

  const computePersistedCombinations = (ignore) => {
    if (!ignore) {
      return toPersistedCombinations(
        combinations.filter(({ id }) => selectedCombinationIds.includes(id)),
      );
    }
    const baseAloneCombination = baseGeneralCost
      ? buildCombinations({
          bases: [baseGeneralCost],
          upsellsByBase: {},
        })
      : [];
    return toPersistedCombinations(baseAloneCombination);
  };

  const onSubmit = async ({ ignore = false } = {}) => {
    const persistedCombinations = computePersistedCombinations(ignore);
    const hasMultipleCombinations = persistedCombinations.length > 1;
    const singleCombination =
      persistedCombinations.length === 1 ? persistedCombinations[0] : null;
    const autoAdditionalInfo = singleCombination
      ? {
          combinationId: singleCombination.id,
          complementaryInsurancePlanId:
            singleCombination.complementaryInsurancePlanId || null,
        }
      : null;

    const newlyConsumedKeys = persistedCombinations.map(combinationKey);
    const existingPersistedCombinations = get(
      project,
      "fields.combinations",
      [],
    );
    const existingKeysSet = new Set(
      existingPersistedCombinations.map(combinationKey),
    );
    const additionalPersistedCombinations = persistedCombinations.filter(
      (combination) => !existingKeysSet.has(combinationKey(combination)),
    );
    const cumulativeCombinations = [
      ...existingPersistedCombinations,
      ...additionalPersistedCombinations,
    ];
    const consumedCombinationKeys = get(
      project,
      "fields.consumedCombinations",
      [],
    );
    const cumulativeConsumedCombinations = uniq([
      ...consumedCombinationKeys,
      ...newlyConsumedKeys,
    ]);

    const profession = get(project, "contact.profession");
    if (
      [
        JOB_LIST.HOSPITAL_CIVIL_SERVICE,
        JOB_LIST.HOSPITAL_PRACTITIONER,
      ].includes(profession) &&
      get(project, "fields.hasCds")
    ) {
      return onNext({
        payload: {
          fields: {
            ...project.fields,
            upsells: ignore ? undefined : selectedModules,
            combinations: cumulativeCombinations,
            currentDevisCombinationKeys: newlyConsumedKeys,
            consumedCombinations: cumulativeConsumedCombinations,
            hasMultipleCombinations,
            ...(singleCombination && {
              selectedCombinationId: singleCombination.id,
            }),
          },
        },
      });
    }
    const insurancePlans = [
      ...get(project, "fields.insurancePlans", []).filter(
        (id) => id !== baseInsurancePlanId,
      ),
      baseInsurancePlanId,
    ];
    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );

    addSubscription({
      variables: {
        data: {
          id: cuid(),
          project: { id: project.id },
          insurancePlan: { id: baseInsurancePlanId },
          ...(autoAdditionalInfo && { additionalInfo: autoAdditionalInfo }),
        },
      },
      onCompleted: ({ addSubscription: { id: subscriptionId } }) => {
        onNext({
          payload: {
            locked: true,
            visible: true,
            fields: {
              ...project.fields,
              insurancePlans,
              ...(!isNil(possibleStatus?.nextStatus) && {
                nextQuoteStatus: possibleStatus.nextStatus,
              }),
              currentSubscription: subscriptionId,
              upsells: ignore ? undefined : selectedModules,
              combinations: cumulativeCombinations,
              currentDevisCombinationKeys: newlyConsumedKeys,
              consumedCombinations: cumulativeConsumedCombinations,
              hasMultipleCombinations,
              ...(singleCombination && {
                selectedCombinationId: singleCombination.id,
              }),
            },
          },
        });
      },
    });
  };

  if (offersLoading || loading || generalCostsLoading) return <Loader />;

  return (
    <>
      <Navbar
        progress={currentStatus?.progress}
        project={project}
        tracer={tracer}
        showCart
        step={3}
        stepCount={5}
        title={"Je renforce ma couverture"}
      />
      <View
        contact={get(project, "contact", {})}
        onNext={onSubmit}
        onBack={onBack}
        upsells={selectedInsurancePlans}
        selectedModules={selectedModules}
        projectId={project?.id}
        handleModule={handleModule}
        insurancePlanId={baseInsurancePlanId}
        combinations={combinations}
        selectedCombinationIds={selectedCombinationIds}
        onToggleCombination={handleCombination}
        baseInsurancePlan={get(baseGeneralCost, "insurancePlan")}
      />
    </>
  );
};

export default Upsells;
